import { useEffect, useState } from "react";
import { registerServiceWorker } from "./adminPush";

const IS_IOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const IS_STANDALONE = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  // iOS legacy
  // eslint-disable-next-line no-restricted-globals
  !!window.navigator.standalone;

/**
 * Returns PWA install state + helpers for the admin dashboard.
 *
 * - On Android Chrome (and Edge/Samsung Internet) captures the
 *   `beforeinstallprompt` event so we can trigger install on a button click.
 * - On iOS Safari, exposes `isIos: true` so the UI can show a manual
 *   "Share → Add to Home Screen" hint.
 * - `installed: true` once the page is running standalone.
 *
 * Side effect: auto-registers the admin service worker on first call so the
 * install criteria are met before the admin enables push notifications.
 */
export function useAdminInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    setInstalled(IS_STANDALONE());
    setIsIos(IS_IOS());

    // Register the service worker as early as possible so Chrome's install
    // heuristic considers the dashboard installable (it needs an active SW
    // with a fetch handler — see /public/admin-sw.js).
    if ("serviceWorker" in navigator) {
      registerServiceWorker().catch(() => {
        /* silent — install will still attempt on user gesture */
      });
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return { outcome: "unavailable" };
    deferred.prompt();
    const result = await deferred.userChoice;
    setDeferred(null);
    return result;
  };

  return {
    /** true → the user can be prompted right now (Android Chrome / Edge) */
    canInstall: !!deferred && !installed,
    /** true → already running as installed PWA */
    installed,
    /** true → iOS Safari (manual Share → Add to Home Screen required) */
    isIos,
    /** trigger install on a button click */
    install,
  };
}
